import { useEffect, useState, useCallback } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import "../../AppPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import { ReactComponent as SelectModelLogo } from "../../../svg/select-model.svg";
import ClipsAutoComplete from "../../../components/autocomplete/ClipsAutoComplete";
import GlimpseBreadCrumbs from "../../../components/glimpse/GlimpseBreadCrumbs";
import { CancelTitleBarMenu } from "../../../components/titleBarMenu/CancelTitleBarMenu";
import { loadClip, loadClips } from "../utils/dataLoader";
import {
  getNavigationUrl,
  checkStatusWhitelist,
} from "../../../store/navigationUtil";
import { useGlimpse } from "../../../store/glimpse/hooks";

export default function GlimpseSelectFilesPage() {
  const navigate = useNavigate();
  const {
    reset: glimpseReset,
    setModel: glimpseSetModel,
    modelClipId,
  } = useGlimpse();

  const { caseId, clipId } = useParams();

  const isSelectButtonDisabled = modelClipId === undefined;
  const [clip, setClip] = useState(null);
  const [clips, setClips] = useState(null);

  const { numberOfChannels, sampleRate } = clip || {};

  const hasGlimpseSuitableClip = clips?.some(
    (c) =>
      c.numberOfChannels === numberOfChannels && c.sampleRate === sampleRate,
  );

  const load = useCallback(async () => {
    const currClip = await loadClip(caseId, clipId);
    if (!currClip) {
      navigate(getNavigationUrl(caseId));
    }
    if (!checkStatusWhitelist(currClip, "EDITING")) {
      navigate(getNavigationUrl(caseId, currClip));
    }
    const currClips = await loadClips(caseId);

    setClips(
      currClips.filter((c) => c.id !== Number(clipId) && c.deleted === false),
    );
    setClip(currClip);
  }, [caseId, clipId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const onClipSelected = async (modelClip) => {
    await glimpseReset();
    if (modelClip) {
      await glimpseSetModel(caseId, modelClip.id);
    }
  };

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} p={2}>
        {clips?.length ? (
          <>
            <GlimpseBreadCrumbs active={1} />
            <Box display="flex" justifyContent="space-around" mt={4}>
              <Paper
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: 500,
                  p: 2,
                }}
              >
                <Box
                  alignSelf="center"
                  display="flex"
                  justifyContent="center"
                  mb={3}
                  width={160}
                >
                  <SelectModelLogo style={{ width: "100%", height: "100%" }} />
                </Box>
                {!hasGlimpseSuitableClip && (
                  <Alert
                    variant="standard"
                    severity="warning"
                    sx={{ mb: 3, boxShadow: 1 }}
                  >
                    <AlertTitle>No Matching Clips</AlertTitle>
                    There are no matching clips to use as a model for GLIMPSE.
                    Models must match the number of channels and sample rate of
                    the clip you wish to GLIMPSE and not currently be in the
                    trash.
                  </Alert>
                )}
                <Typography
                  fontWeight={500}
                  gutterBottom
                  textTransform="uppercase"
                  variant="body1"
                >
                  Select Model
                </Typography>
                <Typography variant="body2">
                  Select the GLIMPSE focusing model file to use
                </Typography>
                {clips && clip && (
                  <ClipsAutoComplete
                    caseId={caseId}
                    clips={clips.filter((c) => c.deleted !== true)}
                    clip={clip}
                    onSelected={(clip) => onClipSelected(clip)}
                  />
                )}
                <Button
                  variant="contained"
                  disabled={isSelectButtonDisabled}
                  onClick={() =>
                    navigate(
                      `/app/cases/${caseId}/clips/${clipId}/glimpse/properties`,
                    )
                  }
                  sx={{ display: "flex", marginTop: 2 }}
                >
                  CHOOSE MODEL
                </Button>
              </Paper>
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={1}
            height={1}
          >
            <CircularProgress />
          </Box>
        )}
      </Stack>
      <ContentTitleBar
        menu={<CancelTitleBarMenu caseId={caseId} clipId={clipId} />}
        name={clip?.description}
        label={clip?.description ? "Create GLIMPSE Job" : ""}
        onBack={() => navigate(`/app/cases/${caseId}/clips/${clipId}/editor`)}
      />
    </Stack>
  );
}
